import S from 's-js';

import UOM from 'lib/UOM';

import { PCCache } from 'data/PCCache';

import { Vessel } from 'data/models/Vessel';
import { OverrideServiceVessel } from 'data/models/OverrideServiceVessel';
import { VesselSearchResultData } from 'data/models/VesselSearchResult';
import { AddVesselRequest } from 'data/models/AddVesselRequest';

import { EditNewVesselOverrides, EditNewVesselOverridesParams, ReadOnlyProperties } from 'portcall/editnewvesseloverrides/model';
import { ExternalVesselSearch, ExternalVesselSearchParams } from 'portcall/externalvesselsearch/model';

export interface NewVesselParams {
    lengthUOM : () => string;
    depthUOM : () => string;
    onSelect : (v : Vessel) => void;
    onDismiss : () => void;
}

export enum NewVesselMode {
    ExternalVesselService = 'external-vessel-service',
    EditExternalVesselOverrides = 'edit-external-vessel-overrides',
    AddManual = 'add-manual'
}

export function NewVessel(
    data: PCCache,
    externalVesselSearchModel : (params : ExternalVesselSearchParams) => ExternalVesselSearch,
    editOverridesModel : (params : EditNewVesselOverridesParams) => EditNewVesselOverrides,
    {
        lengthUOM,
        depthUOM,
        onSelect,
        onDismiss
    } : NewVesselParams
) {
    const
        ExternalVessel = S.value<VesselSearchResultData | null>(null),
        Overrides = new OverrideServiceVessel({
            Id : 0,
            Length: { UOM : S.sample(lengthUOM) },
            Beam: { UOM : S.sample(lengthUOM) },
            MaxDraft: { UOM : S.sample(depthUOM) },
            GrossTonnage: { UOM: UOM.GrossTonnage }
        }),
        Mode = S.data(NewVesselMode.ExternalVesselService),
        ReadOnlyOverrides = S.data<ReadOnlyProperties>({}),
        VesselSearch = externalVesselSearchModel({
            onSelect: v => {
                if (v.VesselId != null) {
                    S.freeze(() => {
                        onSelect(data.Vessels.update1(null, v.Vessel!));
                        onDismiss();
                    });
                    return;
                }
                ExternalVessel(v);

                // figure out which fields are read-only in the overrides list
                const readOnly = {} as ReadOnlyProperties;
                if (v.Length.Num) readOnly.Length = v.Length;
                if (v.Beam.Num) readOnly.Beam = v.Beam;
                if (v.Name) readOnly.Name = v.Name;
                if (v.IMO) readOnly.IMO = v.IMO;
                if (v.MMSID) readOnly.MMSID = v.MMSID;
                if (v.CallSign) readOnly.CallSign = v.CallSign;
                if (v.VIN) readOnly.VIN = v.VIN;
                if (v.VesselType) readOnly.VesselType = v.VesselType;
                if (v.GrossTonnage.Num) readOnly.GrossTonnage = v.GrossTonnage;
                if (v.FlagId !== null) readOnly.VesselFlagId = v.FlagId;
                if (v.MaxDraft.Num) readOnly.MaxDraft = v.MaxDraft;

                ReadOnlyOverrides(readOnly);

                Mode(NewVesselMode.EditExternalVesselOverrides);
            },
            onManualEntry: addManual,
            onDismiss
        }),
        EditOverrides = editOverridesModel({
            Overrides,
            ReadOnly: ReadOnlyOverrides,
            uom: lengthUOM,
            onDismiss
        });

    S(() => {
        if (!VesselSearch.CanSearch()) addManual();
    });

    return { 
        ExternalVessel,
        Overrides,
        CanSave,
        Mode,
        VesselSearch,
        EditOverrides,
        CreateRequest
    };

    function addManual() {
        Mode(NewVesselMode.AddManual);
        ReadOnlyOverrides({});
    }

    function CanSave() {
        return !!(
               (ExternalVessel() && ExternalVessel()!.Length.Num       || Overrides.Length.Num())
            && (ExternalVessel() && ExternalVessel()!.Name             || Overrides.Name())
            && (ExternalVessel() && ExternalVessel()!.Beam.Num         || Overrides.Beam.Num())
            && (ExternalVessel() && ExternalVessel()!.GrossTonnage.Num || Overrides.GrossTonnage.Num())
            && (ExternalVessel() && (ExternalVessel()!.CallSign || ExternalVessel()!.IMO || ExternalVessel()!.MMSID || ExternalVessel()!.VIN) ||
                Overrides.CallSign() || Overrides.IMO() || Overrides.MMSID() || Overrides.VIN())
        );
    }

    function CreateRequest() : AddVesselRequest {
        const ext = ExternalVessel(),
            req = new AddVesselRequest({
                PSixServiceVesselId: ext && ext.PSixServiceVesselId,
                MarineTrafficServiceVesselId: ext && ext.MarineTrafficServiceVesselId,
                LloydServiceVesselId: ext && ext.LloydServiceVesselId
            });

        req.OverrideServiceVessel(Overrides);

        return req;
    }
}
export type NewVessel = ReturnType<typeof NewVessel>;


// WEBPACK FOOTER //
// ./apps/portcall/newvessel/model.ts